// WARNING: PRELOAD_MAP import paths must stay in sync with src/routes/index.tsx.
// Two-way drift here will cause chunk duplication (separate bundles instead of shared).
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCinematicNavigate } from "@/hooks/useCinematicNavigate";
import { useCinematicContext } from "@/components/landing/CinematicTransitionProvider";

// Preload map mirrors the lazyRetry() calls in src/routes/index.tsx exactly.
// WARNING: Keep in sync with src/routes/index.tsx — drift causes chunk duplication.
const PRELOAD_MAP: Record<string, () => Promise<unknown>> = {
  "/pricing": () => import("@/pages/PricingPage"),
  "/coverage": () => import("@/pages/CoveragePage"),
  "/docs": () => import("@/pages/DocsPage"),
  "/blog": () => import("@/pages/BlogIndex"),
  "/about": () => import("@/pages/AboutPage"),
  "/verify": () => import("@/pages/PublicCarrierLookup"),
  "/brokers/signup": () => import("@/pages/dash-v2/BrokerSignupPage"),
};

// Track which routes have already been preloaded so we only fire once per session
const preloadedRoutes = new Set<string>();

interface CinematicLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  children: React.ReactNode;
  className?: string;
  "aria-current"?: React.AnchorHTMLAttributes<HTMLAnchorElement>["aria-current"];
}

export function CinematicLink({ to, children, className, onClick, ...rest }: CinematicLinkProps) {
  const ctx = useCinematicContext();
  const navigate = useNavigate();
  const cinematicNavigate = useCinematicNavigate();
  const hasProvider = ctx !== undefined;

  const handleMouseEnterOrFocus = () => {
    const preload = PRELOAD_MAP[to];
    if (preload && !preloadedRoutes.has(to)) {
      preloadedRoutes.add(to);
      preload().catch(() => {
        // Idempotent — ignore preload failures
      });
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Pass through to onClick if provided
    onClick?.(e);

    // Only intercept plain left-clicks without modifier keys
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }

    e.preventDefault();

    if (hasProvider) {
      cinematicNavigate(to, {
        preload: PRELOAD_MAP[to],
        originPoint: { x: e.clientX, y: e.clientY },
      });
    } else {
      // Defensive fallback when provider is not mounted (e.g., tests without provider)
      navigate(to);
    }
  };

  return (
    <a
      href={to}
      className={className}
      onClick={handleClick}
      onMouseEnter={handleMouseEnterOrFocus}
      onFocus={handleMouseEnterOrFocus}
      {...rest}
    >
      {children}
    </a>
  );
}
